<template>
  <div class="relative -mx-5 h-97dvh">
    <MobileTimelineHeader
      :resources="resources"
      :active-resource="activeResource"
    />
    <div class="relative border-t pt-4 h-full">
      <div ref="eventCalendarRef"></div>
    </div>
    <div class="fixed bottom-12 right-4 z-10 flex gap-3">
      <UnassignedEventsFOB />
      <CreateNewFOB
        :default-date="date"
        :sheet="isSheet"
        :flags-resolved="flagsResolved"
      />
    </div>
    <div>
      <TimelineFooter :last-load="lastLoad" />
    </div>
    <AppointmentDetailsDrawer
      v-if="selectedAppointmentId"
      :appointment-id="selectedAppointmentId"
      :placement="placement"
      @hide-drawer="onHideDrawer"
      @appointment-updated="onAppointmentUpdated"
    />
    <AppointmentCreateModal
      v-if="createSlot && isSheet"
      sheet
      :initial_date="createSlot.date"
      :scheduled_time="createSlot.scheduledTime"
      :technician_id="createSlot.technicianUserId"
      @hide-modal="clearCreateSlot"
      @created="onCreated"
    />
    <!-- Held back until the flag has actually answered: the two surfaces differ
         by more than a class (the desktop one is the jQuery modal, which shows
         itself and registers a backdrop on mount), so mounting the wrong one
         first and swapping would strand that backdrop. -->
    <CalendarEventModal
      v-if="selectedEventId && flagsResolved"
      :calendar-event-id="selectedEventId || ''"
      :sheet="isSheet"
      auto-open
      @close="closeEventModal"
    ></CalendarEventModal>
  </div>
</template>

<script setup lang="ts">
import { debounce } from "lodash";
import { onBeforeUnmount, onMounted, ref, watch } from "vue";
import { getComponentHtml } from "@/lib/getComponentHTML";
import { getQueryVariables } from "@/lib/url";

import AppointmentCreateModal from "@/AppointmentCreateModal/AppointmentCreateModal.vue";
import AppointmentDetailsDrawer from "@/AppointmentDetails/AppointmentDetailsDrawer.vue";
import { useSheetPlacement } from "@/composables/useSheetPlacement";
import CalendarEventModal from "@/Scheduler/CalendarEventModal.vue";
import { useEventCalendar } from "@/SchedulerV2/useEventCalendar";
import { AppointmentV2 } from "@/typings/appointment";
import {
  MbscCalendarEventData,
  MbscCellClickEvent
} from "@mobiscroll/javascript";
import AppointmentEvent from "./AppointmentEvent.vue";
import CreateNewFOB from "./CreateNewFOB.vue";
import MobileTimelineHeader from "./MobileTimelineHeader.vue";
import TimelineFooter from "./TimelineFooter.vue";
import UnassignedEventsFOB from "./UnassignedEventsFOB.vue";

const {
  applyDragGate,
  clearCreateSlot,
  dragOptionsFor,
  createSlot,
  handleCellClick,
  initCalendar,
  destroyCalendar,
  setCalendarDate,
  resources,
  activeResource,
  selectedAppointmentId,
  selectedEventId,
  setTimezoneLabel,
  handleEventRightClick,
  handleEventClick
} = useEventCalendar();
const { isSheet, placement, flagsResolved } = useSheetPlacement();
const props = defineProps<{ date: Date }>();
const emit = defineEmits<{
  (e: "date-navigated", date: Date | string): void;
}>();
const eventCalendarRef = ref<HTMLElement | null>(null);
const lastLoad = ref(new Date());

onMounted(() => {
  initCalendar(eventCalendarRef.value, props.date, {
    renderScheduleEvent: (data: MbscCalendarEventData) =>
      getComponentHtml(AppointmentEvent, { event: data }),
    renderResource: () => "",
    renderHeader: () => "",
    renderDay: () => "",
    onEventClick: handleEventClick,
    onEventRightClick: handleEventRightClick,
    onCellClick: onCellClick,
    onPageLoaded: handlePageLoad,
    ...dragOptionsFor(isSheet.value)
  });
  // Auto-open appointment details drawer if appointment_id query param is present
  const params = getQueryVariables();
  if (params.appointment_id) {
    selectedAppointmentId.value = params.appointment_id;
  }
});

onBeforeUnmount(() => {
  destroyCalendar();
});

watch(
  () => props.date,
  (newVal) => {
    debouncedDateChange(newVal);
  }
);

watch(isSheet, (sheet) => applyDragGate(sheet));

const closeEventModal = () => {
  selectedEventId.value = "";
};
const handlePageLoad = () => {
  setTimeout(() => (lastLoad.value = new Date()), 100);
  setTimezoneLabel();
};
const onAppointmentUpdated = (appointment: AppointmentV2) => {
  // Route drawer-driven navigation through the page's selected date so the
  // header, the modal default, and the URL all follow the calendar
  emit("date-navigated", appointment.arrival_window_start);
};
const debouncedDateChange = debounce(
  (date: Date) => setCalendarDate(date),
  750
);
const onHideDrawer = () => (selectedAppointmentId.value = "");

// Guarded here rather than in the composable: whether a tap opens anything is a
// placement decision, and placement belongs to the call site everywhere else in
// this epic. Timeline.vue carries the same guard for the same reason.
const onCellClick = (args: MbscCellClickEvent) => {
  if (!isSheet.value) return;
  handleCellClick(args);
};

// No refresh here: handleSave emits refresh-schedule-board and the composable
// listens for it, so calling refreshTimeline would fetch the board twice.
const onCreated = () => {
  clearCreateSlot();
};
</script>

<style lang="scss">
.mbsc-schedule-header,
.mbsc-schedule-all-day-cont {
  display: none;
}
.mbsc-schedule-time-indicator-time.mbsc-schedule-time-indicator-time-y {
  background-color: #2563eb; /* A nice shade of blue */
  color: white; /* Sets the text color to white */
  padding: 2px 6px; /* Adds some vertical and horizontal spacing */
  border-radius: 4px; /* Rounds the corners of the container */
}
/*
  This targets the pseudo-element that creates the horizontal line
  and centers it within its container.
*/
.mbsc-schedule-time-indicator-y::after {
  left: 50% !important;
  transform: translateX(-50%) !important;
}

/*
  This re-centers the time label directly above the
  now-centered line.
*/
.mbsc-schedule-time-indicator-time-y {
  left: 50% !important;
  transform: translateX(-42%) !important;
}
</style>
